import { sendMessageInChat, uploadChat } from 'firebase/firebaseFunctions';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Editor, { Toolbar } from 'react-simple-wysiwyg';
import { DeleteIcon, MyAttachFileIcon, MyMicIcon, MyPauseIcon } from '@guryou/html-components';
import EmojiPicker from 'emoji-picker-react';
import { Icon } from '../../components/shared';
import { useCurrentUser } from '@guryou/redux';

// AudioPlayer component (copied from ChatBubble)
const AudioPlayer = ({ audioUrl, onDelete, onSend, isRecording }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [waveformData, setWaveformData] = useState([]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateDuration = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      // Generate waveform data based on duration
      generateWaveformData(audio.duration);
    };

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const generateWaveformData = duration => {
    // Generate random waveform bars for visual representation
    const bars = Array.from({ length: 200 }, () => Math.random() * 20 + 4);
    setWaveformData(bars);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const formatTime = time => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div class="flex items-center gap-3 my-2 w-full">
      <div class="flex items-center gap-3 flex-1 p-3 rounded-lg bg-gray-100/50">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          disabled={isLoading}
          class="flex items-center justify-center w-8 h-8 rounded-full transition-colors bg-gray-200 hover:bg-gray-300 text-gray-700"
        >
          {isLoading ? (
            <div class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          ) : isPlaying ? (
            <Icon name="pause" size="sm" />
          ) : (
            <Icon name="play-circle" size="sm" />
          )}
        </button>

        {/* Waveform Visualization */}
        <div class="flex-1 flex items-center w-full">
          <div class="w-full h-6 flex items-center justify-between cursor-pointer" onClick={togglePlay}>
            {waveformData.map((height, i) => {
              const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
              const barProgress = (i / waveformData.length) * 100;
              const isPlayed = barProgress <= progress;

              return (
                <div
                  key={i}
                  class={`rounded-sm transition-colors ${isPlayed ? 'bg-[var(--feel-primary)]' : 'bg-gray-400'}`}
                  style={{
                    width: `${100 / waveformData.length}%`,
                    height: `${height}px`,
                    minWidth: '1px',
                    maxWidth: '2px',
                    flex: '1',
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Duration */}
        <span class="text-xs font-medium text-gray-600">{formatTime(duration)}</span>

        {/* Action Buttons */}
        <div class="flex items-center gap-1">
          <button
            onClick={onDelete}
            class="flex items-center justify-center w-8 h-8 rounded-full transition-colors bg-gray-200 hover:bg-red-100 text-gray-700 hover:text-red-600"
          >
            <DeleteIcon />
          </button>

          <button
            onClick={onSend}
            class="flex items-center justify-center w-8 h-8 rounded-full transition-colors bg-[var(--feel-primary)] hover:bg-[var(--feel-primary-dark)] text-white"
          >
            <Icon name="send" className="rotate-90 text-white" size="sm" />
          </button>
        </div>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} src={audioUrl} preload="metadata" />
      </div>
    </div>
  );
};

const InputChat = props => {
  const { t } = useTranslation();
  const { currentChat, setMessages } = props;
  const { userId } = useSelector(state => state.auth.data);
  const { user } = useCurrentUser();

  const fileInputRef = React.useRef(null);
  const [html, setHtml] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState();
  const [audioChunks, setAudioChunks] = useState();
  const [audio, setAudio] = useState();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const updateFormattingState = () => {
    const editor = document.querySelector('.rsw-editor');
    if (editor) {
      setIsBold(document.queryCommandState('bold'));
      setIsItalic(document.queryCommandState('italic'));
      setIsUnderline(document.queryCommandState('underline'));
    }
  };

  const convertLinksInText = text => {
    const urlRegex = /(https?:\/\/[^\s<>"]+)/g;

    return text.replace(urlRegex, url => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: var(--feel-primary); text-decoration: underline;">${url}</a>`;
    });
  };

  const handleFormatClick = command => {
    const editor = document.querySelector('.rsw-editor');
    if (editor) {
      document.execCommand(command);
      editor.focus();
      setTimeout(updateFormattingState, 10);
    }
  };

  const handleRecorderPress = async event => {
    event.preventDefault();
    const stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: true,
    });

    const newRecorder = new MediaRecorder(stream);
    newRecorder.start();

    const localAudioChunks = [];
    newRecorder.ondataavailable = event => {
      if (typeof event.data === 'undefined') {
        return;
      }
      if (event.data.size === 0) {
        return;
      }
      localAudioChunks.push(event.data);
    };

    setAudioChunks(localAudioChunks);
    setIsRecording(true);
    setRecorder(newRecorder);
  };

  async function sendMensage() {
    if (!html.trim()) return;
    try {
      const processedContent = convertLinksInText(html);

      setHtml('');
      await sendMessageInChat(currentChat?.id, {
        content: processedContent,
        senderId: +userId,
        createdAt: Date.now(),
        receiveId: currentChat?.receiveId,
        senderName: user?.name,
        senderImage: user?.image,
        receiveName: currentChat?.user?.name,
        receiveImage: currentChat?.user?.image,
        providerName: props?.currentProvider?.name,
      });
    } catch (error) {}
  }

  function onChange(e) {
    const newHtml = e.target.value;
    setHtml(newHtml);
  }

  React.useEffect(() => {
    const editor = document.querySelector('.rsw-editor');
    if (editor) {
      const handleSelectionChange = () => {
        updateFormattingState();
      };

      const handleInput = () => {
        updateFormattingState();
      };

      editor.addEventListener('mouseup', handleSelectionChange);
      editor.addEventListener('keyup', handleSelectionChange);
      editor.addEventListener('focus', handleSelectionChange);
      editor.addEventListener('input', handleInput);

      return () => {
        editor.removeEventListener('mouseup', handleSelectionChange);
        editor.removeEventListener('keyup', handleSelectionChange);
        editor.removeEventListener('focus', handleSelectionChange);
        editor.removeEventListener('input', handleInput);
      };
    }
  }, [html]);

  const handlePausePress = () => {
    if (recorder) {
      recorder.stop();
      recorder.stream.getTracks().forEach(track => track.stop());
      recorder.onstop = () => {
        const blob = new Blob(audioChunks, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(blob);

        const blobFile = {
          file: [],
          blob,
          urls: audioUrl,
          type: 'audio',
        };

        setAudio(blobFile);
        setAudioChunks([]);
      };
    }

    setIsRecording(false);
  };

  const sendAudio = async () => {
    if (!audio) {
      return;
    }

    try {
      const tempId = `temp-${Date.now()}`;
      const newMessage = {
        id: tempId,
        content: audio?.urls,
        senderId: +userId,
        createdAt: Date.now(),
        receiveId: currentChat?.receiveId,
        type: 'audio',
        uploadProgress: 0,
      };
      deleteAudio();

      setMessages(prev => [...prev, newMessage]);

      const docUrl = await uploadChat(audio.blob, progress => {
        setMessages(prev => prev.map(msg => (msg.id === tempId ? { ...msg, uploadProgress: progress } : msg)));
      });

      const finalMessage = {
        content: docUrl,
        senderId: +userId,
        createdAt: Date.now(),
        receiveId: currentChat?.receiveId,
        type: 'audio',
        senderName: user?.name,
        senderImage: user?.image,
        receiveName: currentChat?.user?.name,
        receiveImage: currentChat?.user?.image,
        providerName: props?.currentProvider?.name,
      };

      await sendMessageInChat(currentChat?.id, finalMessage);

      setMessages(prev => prev.map(msg => (msg.id === tempId ? finalMessage : msg)));
    } catch (err) {
      return err;
    }
  };

  const deleteAudio = () => {
    setAudio(null);
    setRecorder(null);
    setIsRecording(false);
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const localDocUrl = URL.createObjectURL(file);

    try {
      const tempId = `temp-${Date.now()}`;
      const newMessage = {
        id: tempId,
        content: file.type.includes('image') ? localDocUrl : file.name,
        senderId: +userId,
        createdAt: Date.now(),
        receiveId: currentChat?.receiveId,
        type: file.type.includes('image') ? 'image' : 'file',
        uploadProgress: 0,
        senderName: user?.name,
        senderImage: user?.image,
        receiveName: currentChat?.user?.name,
        receiveImage: currentChat?.user?.image,
        providerName: props?.currentProvider?.name,
      };

      setMessages(prev => [...prev, newMessage]);

      const docUrl = await uploadChat(file, progress => {
        setMessages(prev => prev.map(msg => (msg.id === tempId ? { ...msg, uploadProgress: progress } : msg)));
      });

      const finalMessage = {
        content: docUrl,
        senderId: +userId,
        createdAt: Date.now(),
        receiveId: currentChat?.receiveId,
        type: file.type.includes('image') ? 'image' : 'file',
      };

      await sendMessageInChat(currentChat?.id, finalMessage);

      setMessages(prev => prev.map(msg => (msg.id === tempId ? finalMessage : msg)));

      URL.revokeObjectURL(localDocUrl);
    } catch (err) {}
  };

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMensage();
    }
  }

  const onEmojiClick = emojiObject => {
    const emoji = emojiObject.emoji;
    setHtml(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  React.useEffect(() => {
    const handleClickOutside = event => {
      if (showEmojiPicker && !event.target.closest('.emoji-picker-container')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  return (
    <div className="sticky bottom-0 bg-white border-t border-gray-200 w-full">
      <div className="px-4 py-3 w-full">
        <div className="flex items-end gap-2 relative w-full">
          {showEmojiPicker && (
            <div className="absolute bottom-full left-0 mb-2 z-10 emoji-picker-container">
              <EmojiPicker onEmojiClick={onEmojiClick} />
            </div>
          )}

          {recorder && isRecording && (
            <div className="flex items-center gap-3 w-full bg-gray-50 rounded-lg p-3 w-full">
              <button
                onClick={handlePausePress}
                className="inline-flex items-center justify-center w-10 h-10 text-white bg-[var(--feel-primary)] rounded-full hover:bg-[var(--feel-primary-dark)] focus:ring-4 focus:outline-none focus:ring-[var(--feel-primary)]/50 transition-colors"
              >
                <MyPauseIcon />
              </button>
              <div className="flex-1 flex items-center gap-1">
                <div className="flex items-center justify-between w-full">
                  {Array.from({ length: 200 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-[var(--feel-primary)] rounded-sm animate-pulse"
                      style={{
                        width: '0.5%',
                        height: `${Math.random() * 20 + 8}px`,
                        animationDelay: `${i * 0.01}s`,
                        minWidth: '1px',
                        maxWidth: '2px',
                        flex: '1',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {audio?.urls && <AudioPlayer audioUrl={audio?.urls} onDelete={deleteAudio} onSend={sendAudio} isRecording={false} />}

          {!recorder && !isRecording && !audio?.urls && (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-1">
                  <div className="flex items-center gap-0.5 px-2 py-1">
                    <button
                      type="button"
                      className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                        isBold ? 'bg-[var(--feel-primary)] text-white hover:bg-[var(--feel-primary-dark)]' : 'text-gray-600 hover:bg-gray-200'
                      }`}
                      title={t('CHAT.bold')}
                      onClick={() => handleFormatClick('bold')}
                    >
                      <span className="text-xs font-bold">B</span>
                    </button>
                    <button
                      type="button"
                      className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                        isItalic ? 'bg-[var(--feel-primary)] text-white hover:bg-[var(--feel-primary-dark)]' : 'text-gray-600 hover:bg-gray-200'
                      }`}
                      title={t('CHAT.italic')}
                      onClick={() => handleFormatClick('italic')}
                    >
                      <span className="text-xs italic">I</span>
                    </button>
                    <button
                      type="button"
                      className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                        isUnderline ? 'bg-[var(--feel-primary)] text-white hover:bg-[var(--feel-primary-dark)]' : 'text-gray-600 hover:bg-gray-200'
                      }`}
                      title={t('CHAT.underline')}
                      onClick={() => handleFormatClick('underline')}
                    >
                      <span className="text-xs underline">U</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="inline-flex items-center justify-center w-8 h-8 text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                    title={t('CHAT.attachFile')}
                  >
                    <MyAttachFileIcon style={{ fontSize: '1rem' }} />
                  </button>
                  <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />

                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="inline-flex items-center justify-center w-8 h-8 text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                    title={t('CHAT.selectEmoji')}
                  >
                    <span role="img" aria-label="emoji picker" className="text-lg">
                      😊
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleRecorderPress}
                    className="inline-flex items-center justify-center w-8 h-8 text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                    title={t('CHAT.recordAudio')}
                  >
                    <MyMicIcon style={{ fontSize: '1rem' }} />
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <div className="w-full bg-gray-50 rounded-lg">
                  <Editor
                    value={html}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('CHAT.typeMessage')}
                    style={{
                      height: '72px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: '0.5rem',
                    }}
                  >
                    <Toolbar
                      style={{
                        backgroundColor: 'transparent',
                        borderBottom: 'none',
                        padding: '4px 8px',
                        display: 'none',
                      }}
                    ></Toolbar>
                  </Editor>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={sendMensage}
                    disabled={!html.trim()}
                    className="inline-flex items-center justify-center w-8 h-8 text-white bg-[var(--feel-primary)] rounded-lg hover:bg-[var(--feel-primary-dark)] focus:ring-4 focus:outline-none focus:ring-[var(--feel-primary)]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title={t('CHAT.sendMessage')}
                  >
                    <Icon name="send" className="rotate-90" size="sm" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InputChat;
